/**
 * Microsoft 365 User Management Types (Frontend)
 */

export interface M365User {
  id: string
  userPrincipalName: string
  displayName: string
  givenName?: string
  surname?: string
  mail?: string
  jobTitle?: string
  department?: string
  officeLocation?: string
  mobilePhone?: string
  businessPhones?: string[]
  accountEnabled: boolean
  createdDateTime?: string
  lastSignInDateTime?: string
  assignedLicenses?: AssignedLicense[]
  usageLocation?: string
  proxyAddresses?: string[] // Email aliases (smtp:, SMTP:, etc.)
}

export interface AssignedLicense {
  skuId: string
  disabledPlans?: string[]
}

export interface M365UserLicense {
  skuId: string
  skuPartNumber: string
  productName?: string
  servicePlans: ServicePlan[]
}

export interface ServicePlan {
  servicePlanId: string
  servicePlanName: string
  provisioningStatus: string
  appliesTo?: string
}

export interface AvailableLicense {
  skuId: string
  skuPartNumber: string
  productName?: string
  totalUnits: number
  consumedUnits: number
  availableUnits: number
  warningUnits: number
  suspendedUnits: number
  capabilityStatus: string
  appliesTo: string
}

export interface UpdateUserRequest {
  displayName?: string
  givenName?: string
  surname?: string
  jobTitle?: string
  department?: string
  officeLocation?: string
  mobilePhone?: string
  usageLocation?: string
}

export interface M365UsersResponse {
  users: M365User[]
  total: number
  nextLink?: string
}

export interface M365UserDetailResponse {
  user: M365User
  licenses: M365UserLicense[]
}

export interface M365AvailableLicensesResponse {
  licenses: AvailableLicense[]
  total: number
}

export interface ResetPasswordResponse {
  temporaryPassword: string
}

export interface AssignLicenseRequest {
  addLicenses?: Array<{ skuId: string; disabledPlans?: string[] }>
  removeLicenses?: string[]
}

// License display name mapping (common SKUs)
export const LICENSE_DISPLAY_NAMES: Record<string, string> = {
  'O365_BUSINESS_ESSENTIALS': 'Microsoft 365 Business Basic',
  'O365_BUSINESS_PREMIUM': 'Microsoft 365 Business Standard',
  'SPB': 'Microsoft 365 Business Premium',
  'ENTERPRISEPACK': 'Office 365 E3',
  'ENTERPRISEPREMIUM': 'Office 365 E5',
  'ENTERPRISEPREMIUM_NOPSTNCONF': 'Office 365 E5 (without Audio Conferencing)',
  'DESKLESSPACK': 'Office 365 F1',
  'FLOW_FREE': 'Power Automate Free',
  'POWER_BI_STANDARD': 'Power BI Free',
  'POWER_BI_PRO': 'Power BI Pro',
  'EXCHANGESTANDARD': 'Exchange Online (Plan 1)',
  'EXCHANGEENTERPRISE': 'Exchange Online (Plan 2)',
  'PROJECTPREMIUM': 'Project Plan 5',
  'PROJECTPROFESSIONAL': 'Project Plan 3',
  'VISIOCLIENT': 'Visio Plan 2',
  'TEAMS_EXPLORATORY': 'Microsoft Teams Exploratory',
  'TEAMS_FREE': 'Microsoft Teams (Free)',
  'AAD_PREMIUM': 'Azure Active Directory Premium P1',
  'AAD_PREMIUM_P2': 'Azure Active Directory Premium P2',
  'EMS': 'Enterprise Mobility + Security E3',
  'EMSPREMIUM': 'Enterprise Mobility + Security E5',
  'INTUNE_A': 'Microsoft Intune',
  'ATP_ENTERPRISE': 'Microsoft Defender for Office 365 (Plan 1)',
  'THREAT_INTELLIGENCE': 'Microsoft Defender for Office 365 (Plan 2)',
  'WIN_DEF_ATP': 'Microsoft Defender for Endpoint',
  'IDENTITY_THREAT_PROTECTION': 'Microsoft 365 E5 Security',
  'INFORMATION_PROTECTION_COMPLIANCE': 'Microsoft 365 E5 Compliance',
  'M365_F1': 'Microsoft 365 F1',
  'SPE_E3': 'Microsoft 365 E3',
  'SPE_E5': 'Microsoft 365 E5',
  'SPE_F1': 'Microsoft 365 F3',
}

/**
 * Get friendly display name for a license SKU
 */
export function getLicenseDisplayName(skuPartNumber: string): string {
  return LICENSE_DISPLAY_NAMES[skuPartNumber] || skuPartNumber
}

// =============================================================================
// Groups & Directory Roles
// =============================================================================

export interface M365Group {
  id: string
  displayName: string
  description?: string
  groupTypes: string[]
  mailEnabled: boolean
  securityEnabled: boolean
  mail?: string
  membershipRule?: string
}

export interface M365DirectoryRole {
  id: string
  displayName: string
  description?: string
  roleTemplateId: string
}

export interface M365UserGroupsResponse {
  groups: M365Group[]
  directoryRoles: M365DirectoryRole[]
  total: number
}

// =============================================================================
// Devices
// =============================================================================

export interface M365Device {
  id: string
  displayName: string
  deviceId: string
  operatingSystem?: string
  operatingSystemVersion?: string
  trustType?: string
  isCompliant?: boolean
  isManaged?: boolean
  approximateLastSignInDateTime?: string
  registeredOwners?: string[]
  manufacturer?: string
  model?: string
  enrollmentType?: string
}

export interface M365UserDevicesResponse {
  ownedDevices: M365Device[]
  registeredDevices: M365Device[]
  total: number
}

// =============================================================================
// Sign-in Activity Logs
// =============================================================================

export interface M365SignIn {
  id: string
  createdDateTime: string
  userPrincipalName: string
  userId: string
  appDisplayName?: string
  appId?: string
  ipAddress?: string
  clientAppUsed?: string
  conditionalAccessStatus?: string
  isInteractive: boolean
  resourceDisplayName?: string
  status: {
    errorCode: number
    failureReason?: string
    additionalDetails?: string
  }
  location?: {
    city?: string
    state?: string
    countryOrRegion?: string
  }
  deviceDetail?: {
    browser?: string
    operatingSystem?: string
    deviceId?: string
    displayName?: string
    isCompliant?: boolean
    isManaged?: boolean
    trustType?: string
  }
  riskDetail?: string
  riskLevelAggregated?: string
  riskLevelDuringSignIn?: string
  riskState?: string
}

export interface M365UserSignInsResponse {
  signIns: M365SignIn[]
  total: number
  nextLink?: string
}

// =============================================================================
// Mailbox
// =============================================================================

export interface M365MailboxSettings {
  automaticRepliesSetting: {
    status: 'disabled' | 'alwaysEnabled' | 'scheduled'
    externalAudience?: 'none' | 'contactsOnly' | 'all'
    scheduledStartDateTime?: { dateTime: string; timeZone: string }
    scheduledEndDateTime?: { dateTime: string; timeZone: string }
    internalReplyMessage?: string
    externalReplyMessage?: string
  }
  language?: {
    locale: string
    displayName: string
  }
  timeZone?: string
  delegateMeetingMessageDeliveryOptions?: string
}

export interface M365MailboxInfo {
  settings: M365MailboxSettings
  storageUsedInBytes?: number
  storageQuotaInBytes?: number
  forwardingAddress?: string
  forwardingEnabled?: boolean
  hiddenFromAddressListsEnabled?: boolean
}

export interface M365UserMailboxResponse {
  mailbox: M365MailboxInfo
}

// =============================================================================
// OneDrive
// =============================================================================

export interface M365OneDriveInfo {
  driveId: string
  webUrl: string
  quota: {
    used: number
    total: number
    remaining: number
    state: 'normal' | 'nearing' | 'critical' | 'exceeded'
  }
  owner?: {
    user?: {
      id?: string
      displayName: string
    }
  }
  // Additional fields from Reports API
  fileCount?: number
  activeFileCount?: number
  lastActivityDate?: string | null
}

export interface M365UserOneDriveResponse {
  oneDrive: M365OneDriveInfo | null
}

// =============================================================================
// Authentication Methods
// =============================================================================

export type AuthMethodType =
  | 'phone'
  | 'email'
  | 'fido2'
  | 'microsoftAuthenticator'
  | 'softwareOath'
  | 'temporaryAccessPass'
  | 'password'
  | 'windowsHelloForBusiness'

export interface M365AuthenticationMethod {
  id: string
  methodType: AuthMethodType
  // Phone method fields
  phoneNumber?: string
  phoneType?: 'mobile' | 'alternateMobile' | 'office'
  // Email method fields
  emailAddress?: string
  // Authenticator/FIDO2 fields
  displayName?: string
  deviceTag?: string
  // Common fields
  createdDateTime?: string
  // TAP specific
  isUsable?: boolean
  isUsableOnce?: boolean
  lifetimeInMinutes?: number
  startDateTime?: string
}

export interface M365AuthMethodsResponse {
  methods: M365AuthenticationMethod[]
}

export interface CreateTAPRequest {
  lifetimeInMinutes?: number
  isUsableOnce?: boolean
}

export interface CreateTAPResponse {
  id: string
  temporaryAccessPass: string
  createdDateTime: string
  startDateTime: string
  lifetimeInMinutes: number
  isUsableOnce: boolean
  isUsable: boolean
  methodUsabilityReason?: string
}
